import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {getCookie} from '../../api/cookie';
import {changeField, initialize, createHistory, getBotList, readHistoryList} from '../../modules/chat/chat';
import {changeField as changeFieldType, initialize as initializeFeedback} from '../../modules/chat/feedback';
import Chat from '../../components/chat/Chat';
import { WEBSOCKET_URL } from '../../api/url';
import LoadingComponent from '../../components/common/Loading';
import validateInput from '../../utils/validateInput';

export default function ChatContainer() {
    const dispatch = useDispatch();

    const navigate = useNavigate();

    const { id } = useParams(); // 히스토리 번호
    const location = useLocation();
    const newChatKey = location.state?.newChatKey; // 새 채팅 강제 초기화 트리거

    const { message, createHistoryResponse, createHistoryErrorResponse, createHistoryLoading, getBotListResponse, getBotListErrorResponse, getBotListLoading } = useSelector(({ chat, loading }) => ({
        message: chat.message,
        createHistoryResponse: chat.createHistoryResponse,
        createHistoryErrorResponse: chat.createHistoryErrorResponse,
        createHistoryLoading: loading['chat/CREATE_HISTORY'],
        getBotListResponse: chat.getBotListResponse,
        getBotListErrorResponse: chat.getBotListErrorResponse,
        getBotListLoading: loading['chat/GET_BOT_LIST']
    }));

    const [chatList, setChatList] = useState([]);
    const [isSendMessage, setIsSendMessage] = useState(false);           // 질문 전송 여부(질문 전송 or 전송중 : true, 질문 전송 후 응답 수신 : false) -> 로딩이나 전송/중지 버튼 표시 결정

    // 스트리밍 관련 상태
    const [isStreaming, setIsStreaming] = useState(false);               // 스트리밍 진행중 여부
    const [streamingType, setStreamingType] = useState(null);            // STREAM_START, STREAM_END, ANSWER_END
    const [streamingChatId, setStreamingChatId] = useState(null);        // 스트리밍 진행 중인 채팅의 질문 id
    const [isWaitingData, setIsWaitingData] = useState(false);           // 스트리밍 후 DATA 혹은 REFERENCES 기다리중 여부

    // 봇 관련 상태
    const [currentBot, setCurrentBot] = useState({ id: null, type: '' }); // 현재 봇 정보

    const webSocket = useRef(null);
    const chatListRef = useRef(chatList);

    // 스트리밍 관련 참조
    const thinkChunks = useRef([]);
    const answerChunks = useRef([]);

    const token = getCookie('accessToken');

    const [currentHistoryId, setCurrentHistoryId] = useState(null);
    const [loading, setLoading] = useState(false);
    const initialOptions = {
        initMsg: '',
        target: null,
        keyValue: null,
        chatId: null
    }

    const handleChange = (e) => {
        const {name, value} = e.target;
        dispatch(changeField({
            name,
            value,
        }));
    };

    const validateBeforeSend = (message) => {
        if (!token) {
            alert('세션이 만료되었습니다. 로그인 후 다시 시도해 주세요.');
            navigate('/auth/login');
            return false;
        }

        if (!message.trim()) {
            alert('질문을 입력한 후 전송해 주세요.');
            return false;
        }

        return true;
    };

    const onChangeHistory = (historyId) => {
        setChatList([]);
        closeConnection();
        setCurrentHistoryId(historyId);
    };

    const closeConnection = () => {
        if (webSocket.current) webSocket.current.close();
        setIsSendMessage(false);
    };

    const createNewHistory = (title) => {
        setLoading(true);
        dispatch(createHistory({title}));
    };

    const changeCurrentBot = (selectedBot) => {
        const { id, type } = selectedBot;
        setCurrentBot({ id, type });
    };

    const findBotId = (botType) => { // bot의 type을 가지고 id를 찾는 함수
        const targetBot = getBotListResponse.find(item => item.type === botType);
        return targetBot ? targetBot.id : null;
    };

    const isEmptyObject = (object) => { // 빈 객체인지 확인하는 함수
        return object && Object.keys(object).length === 0;
    };

    const createTableData = (data) => { // 테이블 데이터 생성하는 함수
        const { table, dataType } = data;

        if (table.length < 1 && Object.keys(dataType).length === 0) { // table이 빈 배열이고 dataType도 빈 객체인 경우
            return [];
        }

        if (table.length < 1) {
            return [Object.keys(dataType).reduce((acc, name) => {
                acc[name] = null;
                return acc;
            }, {})];
        }

        return table;
    };

    const handleStreamingState = { // 스트리밍 및 답변 수신 상태 처리 함수 : STREAM_START, STREAM_END, ANSWER_END
        STREAM_START: (type, chatId) => {
            setIsStreaming(true);
            setStreamingType(type);
            setStreamingChatId(chatId);
        },  

        STREAM_END: (type, chatId) => {
            setIsStreaming(false);
            setStreamingType(type);
            setIsWaitingData(true);
        },

        ANSWER_END: (type, chatId) => {
            setStreamingType(type);
            setIsWaitingData(false);
        }
    }

    const handleStreamingResponse = { // 스트리밍 타입 응답 처리 함수 : THINK, SUMMARY
        THINK: (receivedData, commonState, currentChat) => {
            const { string } = receivedData;

            if (string) thinkChunks.current.push(string);        // 배열에 청크 추가
        
            const newThinkString = thinkChunks.current.join(''); // 배열의 모든 청크를 하나의 문자열로 결합
            
            return {
                ...currentChat,
                ...commonState,
                thinkString: newThinkString,
                isRequiredFeedback: false
            };
        },

        SUMMARY: (receivedData, commonState, currentChat) => {
            const { string } = receivedData;

            if (string) answerChunks.current.push(string);
        
            const newAnswerString = answerChunks.current.join('');

            return {
                ...currentChat,
                ...commonState,
                answer: newAnswerString,
                isRequiredFeedback: false
            };
        }
    };

    const handleResponse = { // 일반 타입 응답 처리 함수 : DATA, REFERENCES, FAILURE
        DATA: (receivedData, commonState, currentChat) => {
            const { data } = receivedData;
          
            return {
                ...currentChat,
                ...commonState,
                data: {
                    table: createTableData(data),
                    dataType: data.dataType
                },
                isRequiredFeedback: false
            };
        },

        REFERENCES: (receivedData, commonState, currentChat) => {
            const { data } = receivedData;
            
            return {
                ...currentChat,
                ...commonState,
                referenceList: data,
                isRequiredFeedback: false
            };
        },

        FAILURE: (receivedData, commonState, currentChat) => {
            const { message } = receivedData;
            
            return {
                ...currentChat,
                ...commonState,
                answer: message,
                isRequiredFeedback: false
            };
        }
    };

    const saveResponse = (e) => {
        const receivedData = JSON.parse(e.data);

        const { type, historyId, chatId, answerId, botType  } = receivedData;
        
        const commonState = {
            historyId,
            chatId,
            answerId,
            botType
        };
        
        // 현재 처리 중인 채팅 찾기
        const currentChatList = chatListRef.current;
        const currentChatIndex = currentChatList.length - 1;
        const currentChat = currentChatList[currentChatIndex];

        // 스트리밍 타입 확인
        const isStreamingType = type === 'STREAM_START' || type === 'STREAM_END' || type === 'THINK' || type === 'SUMMARY';

        // 스트리밍 및 답변 수신 상태 확인
        const isChangeStreamingState = type === 'STREAM_START' || type === 'STREAM_END' || type === 'ANSWER_END';

        if (isChangeStreamingState) {
            handleStreamingState[type](type, chatId);
        }
        
        // 타입별 처리
        if (isStreamingType && handleStreamingResponse[type]) { // 1. 스트리밍 타입
            const updatedChat = handleStreamingResponse[type](receivedData, commonState, currentChat);
            
            setChatList(prevChatList => prevChatList.map((chat, index) =>
                index === currentChatIndex ? updatedChat : chat
            ).slice(-10));
            
            setIsSendMessage(true);
        }
            
        if (!isStreamingType && handleResponse[type]) { // 2. 일반 타입
            const updatedChat = handleResponse[type](receivedData, commonState, currentChat);
            
            updatedChat.isOpenDropdown = false;
            
            setChatList(prevChatList => prevChatList.map((chat, index) =>
                index === currentChatIndex ? updatedChat : chat
            ).slice(-10));

            setIsSendMessage(false);
        }
    };

    const normalizeHistoryChat = (historyChat) => { // 지난 채팅 내역을 통일된 형식으로 변환하는 함수
        const { chatId, answerId, question, answer, bot, feedbackType } = historyChat;
        const { think, summary, data, references } = answer;

        setIsStreaming(false);
        setStreamingType(null);
        setStreamingChatId(null);
        setIsWaitingData(false);

        return {
            historyId: id ?? null,
            chatId,
            answerId,
            question,
            thinkString: think ?? '',
            answer: summary ?? '',
            data: data
                    ? {
                        table: createTableData(data),
                        dataType: data.dataType
                    } 
                    : null,
            referenceList: references ?? [],
            viewType: 'TABLE',
            botType: bot.type,
            feedbackType,
            isRequiredFeedback: false,                          // TODO : 요구사항에 따라 true or false
            isOpenDropdown: true
        };
    };

    const sendMessage = (message, historyId) => {
        // 청크 배열 초기화
        thinkChunks.current = [];
        answerChunks.current = [];

        // 스트리밍 관련 상태 초기화
        setIsStreaming(false);
        setStreamingType(null);
        setStreamingChatId(null);
        setIsWaitingData(false);

        webSocket.current = new WebSocket(WEBSOCKET_URL);

        webSocket.current.onopen = () => {
            webSocket.current.send(JSON.stringify({ 
                token, 
                message, 
                historyId,
                botId: currentBot.id
            }));

            setChatList(prevChatList => {
                const newChatObject = {
                    historyId: id ?? null,
                    chatId: null,
                    answerId: null,
                    question: message,
                    thinkString: '',
                    answer: '',
                    data: null,
                    referenceList: null,
                    viewType: 'TABLE',
                    botType: currentBot.type,
                    feedbackType: null,
                    isRequiredFeedback: false,
                    isOpenDropdown: true
                };
                
                return [...prevChatList, newChatObject].slice(-10);
            });

            setIsSendMessage(true);
            dispatch(changeField({
                name: 'message',
                value: ''
            }));
            dispatch(initializeFeedback());
        };

        webSocket.current.onclose = (error) => {
            console.log('WebSocket 연결 종료', error);
            setIsSendMessage(false);
        };

        webSocket.current.onerror = (error) => {
            console.log('WebSocket 에러', error);
            closeConnection();
            setIsSendMessage(false);
        };

        webSocket.current.onmessage = (e) => { // 데이터 수신했을 때 발생
            saveResponse(e);
            dispatch(changeField({
                name: 'message',
                value: '',
            }));
        };

        return () => {
            if (webSocket.current) closeConnection();
        }
    };

    const handleSendMessage = (message) => {        
        if (!currentHistoryId) { 
            createNewHistory(message);
            return;
        }

        if (!currentBot.type) return;

        if (!validateBeforeSend(message)) return;

        sendMessage(message.trim(), id ?? currentHistoryId);
    };

    const handleResendMessage = (suggest) => {
        if (webSocket.current) closeConnection();
        if (!suggest.message || !currentBot || !currentBot.id) return;
        handleSendMessage(suggest.date ? `${suggest.date} ${suggest.message}` : suggest.message,
                          {initMsg: suggest.initMsg ?? '', chatId: suggest.chatId, target: suggest.target, keyValue: suggest.keyValue});
    };

    useEffect(() => {
        if (createHistoryErrorResponse) {
            setCurrentHistoryId(null);
            alert('히스토리 생성에 실패했습니다.'); // temp
            setLoading(false);
            closeConnection();            
            return;
        }

        if (createHistoryResponse && createHistoryResponse.id) {
            if (typeof createHistoryResponse.id !== 'number') return;
            onChangeHistory(createHistoryResponse.id);
            if (currentBot.type) {
                sendMessage(message ? message.trim() : message, createHistoryResponse.id, initialOptions);
                setLoading(false);
                dispatch(readHistoryList());
            }
            return;
        }

        // return () => {
        //     handleChange({ target: { name: 'createHistoryErrorResponse', value: null }});
        //     handleChange({ target: { name: 'createHistoryResponse', value: null }});
        // };
    }, [createHistoryResponse, createHistoryErrorResponse]);

    useEffect(() => {
        chatListRef.current = chatList;
    }, [chatList]);

    useEffect(() => {
        if (!getBotListLoading && getBotListResponse) {
            const firstBot = getBotListResponse[0];
            const { id, type } = firstBot;
            setCurrentBot({ id, type });
        }
    }, [getBotListResponse, getBotListLoading]);

    useEffect(() => {
        setChatList([]);
        setCurrentHistoryId(id ?? null);
    }, [id, newChatKey]);

    useEffect(() => {
        dispatch(getBotList());
    }, []);

    return (
        <>
            {loading ? 
            <div style={{marginLeft: '100px', height: 'calc(100vh - 100px)', justifyContent: 'center', display: 'flex', flexDirection: 'column', position: 'fixed', alignItems: 'center', width: '100%', scrollbarWidth: 'none', backgroundColor: 'transparent', zIndex: '20'}}>
                <LoadingComponent/>
            </div> : null}
            <Chat
                historyId={id}
                message={message}
                chatList={chatList}
                setChatList={setChatList}
                isSendMessage={isSendMessage}
                handleChange={handleChange}
                handleSendMessage={handleSendMessage}
                handleResendMessage={handleResendMessage}
                handleChangeHistoryId={onChangeHistory}
                closeConnection={closeConnection}
                getBotListResponse={getBotListResponse}
                currentBot={currentBot}
                changeCurrentBot={changeCurrentBot}
                findBotId={findBotId}
                normalizeHistoryChat={normalizeHistoryChat}
                isStreaming={isStreaming}
                streamingType={streamingType}
                streamingChatId={streamingChatId}
                isWaitingData={isWaitingData}
                setCurrentHistoryId={setCurrentHistoryId}
            />
        </>
    );
}
